<script setup lang="ts">
import { Search } from '@element-plus/icons-vue';
import {
  ref, computed, watch, onMounted, onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
import { debounce } from '../../shared/utility-functions';

const props = defineProps({
  showDateOnly: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

const sortOptions = [
  { value: 'transaction', label: 'Transaction' },
  { value: 'date', label: 'Date' },
  { value: 'user', label: 'User' },
  { value: 'total', label: 'Total' },
  { value: 'item', label: 'Item' },
  { value: 'details', label: 'Details' },
  { value: 'email', label: 'Email' },
  { value: 'type', label: 'Type' },
];
const sort = computed({
  get: () => store.getters['activityLogs/GET_SORT'],
  set: (value) => store.dispatch('activityLogs/SET_SORT', (value)),
});

const date = computed({
  get: () => store.getters['activityLogs/GET_DATE'],
  set: (value) => store.dispatch('activityLogs/SET_DATE', (value)),
});

const search = ref(store.getters['activityLogs/GET_SEARCH']);

const debouncedSearch = ref(search.value);
const datePickerRef = ref(null);
const datePickerContainerRef = ref(null);

// Popper options to control placement
const popperOptions = {
  placement: 'bottom-end', // Position below, aligned to right
  strategy: 'fixed', // Use fixed positioning instead of absolute
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 8], // 0px horizontal, 8px vertical offset
      },
    },
    {
      name: 'preventOverflow',
      options: {
        padding: 8,
        boundary: 'viewport',
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top-end', 'bottom-start', 'top-start'],
      },
    },
  ],
};

// Set default date range to last 30 days if no date is set
onMounted(() => {
  if (!date.value) {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    store.dispatch('activityLogs/SET_DATE', ([startDate, endDate]));
  }
  
  // Debug: Watch for popper creation
  setTimeout(() => {
    const popper = document.querySelector('.c-date-range-popper');
    console.log('[DatePicker] Initial popper check:', {
      exists: !!popper,
      position: popper?.style.position,
      transform: popper?.style.transform,
    });
  }, 1000);
});

const onOpenCalendar = () => {
  console.log('[DatePicker] Opening calendar', {
    hasRef: !!datePickerRef.value,
    containerRef: datePickerContainerRef.value,
    containerRect: datePickerContainerRef.value?.getBoundingClientRect(),
  });
  
  // Try different methods to open the picker
  if (datePickerRef.value) {
    // Method 1: Try handleOpen if available
    if (typeof datePickerRef.value.handleOpen === 'function') {
      console.log('[DatePicker] Using handleOpen method');
      datePickerRef.value.handleOpen();
      
      // Debug: Check popper after opening
      setTimeout(() => {
        const popper = document.querySelector('.c-date-range-popper');
        const wrapper = document.querySelector('.c-hidden-date-picker-wrapper');
        console.log('[DatePicker] After open:', {
          popperExists: !!popper,
          popperRect: popper?.getBoundingClientRect(),
          popperStyle: {
            position: popper?.style.position,
            top: popper?.style.top,
            left: popper?.style.left,
            transform: popper?.style.transform,
          },
          wrapperRect: wrapper?.getBoundingClientRect(),
          buttonRect: datePickerContainerRef.value?.getBoundingClientRect(),
        });
      }, 100);
      
      return;
    }
    
    // Method 2: Try focus
    if (typeof datePickerRef.value.focus === 'function') {
      console.log('[DatePicker] Using focus method');
      datePickerRef.value.focus();
      return;
    }
    
    // Method 3: Try to click the input
    try {
      const el = datePickerRef.value.$el;
      if (el && typeof el.querySelector === 'function') {
        const pickerInput = el.querySelector('input');
        if (pickerInput) {
          console.log('[DatePicker] Clicking input element');
          pickerInput.click();
          return;
        }
      }
    } catch (e) {
      console.error('[DatePicker] Error finding input:', e);
    }
    
    console.error('[DatePicker] Could not open picker - no method worked');
  }
};

// Date range validation
const onDateChange = (newDateRange) => {
  if (newDateRange && Array.isArray(newDateRange) && newDateRange.length === 2) {
    const [startDate, endDate] = newDateRange.map((d) => new Date(d));

    // Check if range is more than 1 year
    const maxEndDate = new Date(startDate);
    maxEndDate.setFullYear(maxEndDate.getFullYear() + 1);

    if (endDate > maxEndDate) {
      // Adjust end date to be exactly 1 year from start date
      store.dispatch('activityLogs/SET_DATE', ([startDate, maxEndDate]));
    } else {
      store.dispatch('activityLogs/SET_DATE', (newDateRange));
    }
  }
};

const formatDateShort = (dateValue) => {
  if (!dateValue) return '';
  const d = new Date(dateValue);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

watch(
  search,
  debounce((newValue) => {
    debouncedSearch.value = newValue;
    store.dispatch('activityLogs/SET_SEARCH', newValue);
  }, 300),
);

onUnmounted(() => {
  store.dispatch('activityLogs/SET_SORT', ('transaction'));
  store.dispatch('activityLogs/SET_SEARCH', (''));
});

</script>

<template lang="pug">
//- Show only date range card when showDateOnly is true
template(v-if="showDateOnly")
  //- Date Range Display Card
  .c-date-range-card(v-if="date && date.length === 2")
    .c-date-range-card__content
      .c-date-range-card__dates
        img.c-date-range-card__icon(src="@/assets/icons/calendar.svg" alt="Calendar")
        .c-date-range-card__badges
          .c-date-range-badge {{ formatDateShort(date[0]) }}
          span.c-date-range-arrow →
          .c-date-range-badge {{ formatDateShort(date[1]) }}
      .c-date-range-card__button-wrapper(ref="datePickerContainerRef")
        el-button.c-date-range-card__button(
          @click="onOpenCalendar"
        )
          img.c-date-range-card__button-icon(src="@/assets/icons/calendar.svg" alt="Calendar")
          span Change Date Range
        
        //- Hidden Date Picker (positioned here so popper appears in right place)
        .c-hidden-date-picker-wrapper
          el-date-picker.c-activity-filters__date-picker(
            ref="datePickerRef"
            v-model="date"
            placeholder="Pick a day"
            size="large"
            type="daterange"
            range-separator="To"
            start-placeholder="Start date"
            end-placeholder="End date"
            @change="onDateChange"
            teleported
            popper-class="c-date-range-popper"
            :popper-options="popperOptions"
          )

//- Show search and sort filters when showDateOnly is false
template(v-else)
  .c-activity-filters
    .c-activity-filters__search
      el-input(
        v-model="search"
        :prefix-icon="Search"
        type="text"
        placeholder="Search transactions, users, items..."
        clearable
        size="large"
      )

    .c-activity-filters__actions
      el-select.c-activity-filters__sort(
        v-model="sort"
        placeholder="Sort by"
        size="large"
      )
        el-option(
          v-for="item in sortOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        )
</template>

<style lang="scss" scoped>
// Color Variables - Match Dashboard.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$color-purple-text: #4349dd;

.c-activity-filters {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 0;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }

  &__search {
    flex: 1;
    min-width: 0;

    :deep(.el-input) {
      .el-input__wrapper {
        border-radius: 10px;
        border: 1.5px solid $gray-200;
        background: #F0F0F3;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
        padding: 0 12px;
        height: 40px;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
          border-color: $color-purple-text;
          box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
        }

        &.is-focus {
          border-color: $color-purple-text;
          box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
        }
      }

      .el-input__inner {
        height: 100%;
        font-size: 0.875rem;
        color: $gray-700;
        background: transparent;

        &::placeholder {
          color: $gray-400;
          font-weight: 400;
        }
      }

      .el-input__prefix {
        color: $gray-400;
        font-size: 1.125rem;

        .el-icon {
          font-size: 1.125rem;
        }
      }

      .el-input__suffix {
        .el-input__clear {
          color: $gray-400;
          transition: color 0.2s ease;

          &:hover {
            color: $color-purple-text;
          }
        }
      }
    }
  }

  &__actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;

    @media (max-width: 767px) {
      width: 100%;
    }
  }

  &__sort {
    width: 240px;

    @media (max-width: 767px) {
      width: 100%;
    }

    :deep(.el-select__wrapper) {
      border-radius: 10px;
      border: 1.5px solid $gray-200;
      background: #F0F0F3;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
      padding: 0 12px;
      height: 40px;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover {
        border-color: $color-purple-text;
        box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
      }

      &.is-focused {
        border-color: $color-purple-text;
        box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
      }

      .el-select__selected-item {
        font-size: 0.875rem;
        color: $gray-700;
        font-weight: 500;
      }

      .el-select__placeholder {
        color: $gray-400;
        font-size: 0.875rem;
        font-weight: 400;
      }

      .el-select__suffix {
        .el-icon {
          color: $gray-400;
          font-size: 1rem;
          transition: transform 0.2s ease, color 0.2s ease;
        }
      }

      &.is-focused .el-select__suffix .el-icon {
        color: $color-purple-text;
      }
    }
  }

  &__date-picker {
    // Keep the component functional but hide its input
  }
}

// Hide the wrapper that contains the date picker but keep it positioned near the button
.c-hidden-date-picker-wrapper {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 0;
  height: 0;
  overflow: visible;
  opacity: 0;
  pointer-events: auto;
  z-index: 1;
  
  :deep(.el-input) {
    width: 0;
    height: 0;
    opacity: 0;
  }
}

// Date Range Card - Enhanced Modern Design
.c-date-range-card {
  background: white;
  border: 1px solid #dfdfdf;
  border-radius: 16px;
  padding: 12px;
  box-shadow: none;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: none;
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.25rem;
    flex-wrap: wrap;
  }

  &__dates {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }

  &__icon {
    width: 1.375rem;
    height: 1.375rem;
    color: $gray-500;
    flex-shrink: 0;
    opacity: 0.8;
  }

  &__badges {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  &__button-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  &__button {
    height: auto;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1.5px solid $gray-200;
    background: white;
    font-size: 0.875rem;
    font-weight: 600;
    color: $gray-700;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);

    &:hover {
      background: $color-teal-accent;
      border-color: $color-teal-accent;
      color: white;
      box-shadow: 0 2px 4px 0 rgba(1, 190, 176, 0.2);
      transform: translateY(-1px);

      .c-date-range-card__button-icon {
        filter: brightness(0) invert(1);
      }
    }

    &:active {
      background: color.adjust($color-teal-accent, $lightness: -5%);
      border-color: color.adjust($color-teal-accent, $lightness: -5%);
      transform: translateY(0);
      box-shadow: 0 1px 2px 0 rgba(1, 190, 176, 0.2);
    }

    :deep(span) {
      font-weight: 600;
      line-height: 1.25rem;
      margin-left: 0.5rem;
    }
  }

  &__button-icon {
    width: 0.875rem;
    height: 0.875rem;
    color: $gray-600;
    flex-shrink: 0;
    margin-right: 0;
  }

  @media (max-width: 767px) {
    &__content {
      flex-direction: column;
      align-items: flex-start;
    }

    &__button-wrapper {
      width: 100%;
    }

    &__button {
      width: 100%;
      justify-content: center;
    }
  }
}

.c-date-range-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  background: linear-gradient(135deg, #e8e9fd 0%, #f0f1fe 100%);
  color: $color-purple-text;
  border: 1.5px solid rgba(67, 73, 221, 0.3);
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.125rem;
  white-space: nowrap;
  box-shadow: 0 1px 2px 0 rgba(67, 73, 221, 0.08);
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(67, 73, 221, 0.5);
    box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.12);
    transform: translateY(-1px);
  }
}

.c-date-range-arrow {
  color: $gray-400;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1rem;
  opacity: 0.7;
}

// The date picker input is hidden via the wrapper
// The popper (dropdown) will still show because it's teleported to body

// Date Picker Dropdown Styling - Enhanced Modern Design
.c-date-range-popper {
  // Force popper to appear at the right edge
  transform-origin: top right !important;
}

:deep(.el-picker-panel) {
  border-radius: 12px;
  border: 1px solid $gray-200;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

:deep(.el-date-range-picker__header) {
  padding: 16px 12px 12px;

  .el-picker-panel__icon-btn {
    color: $gray-500;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      color: $color-teal-accent;
      background-color: rgba(1, 190, 176, 0.08);
    }
  }
}

:deep(.el-date-range-picker__content) {
  padding: 12px;
}

:deep(.el-date-table) {
  th {
    color: $gray-500;
    font-weight: 600;
    font-size: 0.8125rem;
    padding: 8px 0;
  }

  td {
    padding: 4px;

    .el-date-table-cell {
      height: 36px;
      width: 36px;
      border-radius: 8px;
      transition: all 0.2s ease;
    }

    &.available:hover {
      .el-date-table-cell__text {
        color: white;
        background-color: $color-teal-accent;
        border-radius: 8px;
        font-weight: 600;
      }
    }

    &.in-range {
      .el-date-table-cell__text {
        background-color: rgba(1, 190, 176, 0.12);
        color: $color-teal-accent;
        font-weight: 500;
      }
    }

    &.start-date,
    &.end-date {
      .el-date-table-cell__text {
        background-color: $color-teal-accent;
        color: white;
        font-weight: 700;
        box-shadow: 0 2px 4px rgba(1, 190, 176, 0.3);
      }
    }

    &.today {
      .el-date-table-cell__text {
        color: $color-teal-accent;
        font-weight: 700;
        position: relative;

        &::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background-color: $color-teal-accent;
        }
      }
    }

    &.disabled {
      .el-date-table-cell__text {
        color: $gray-300;
        cursor: not-allowed;
      }
    }
  }
}

// Select Dropdown Styling - Enhanced Modern Design
:deep(.el-select-dropdown) {
  border-radius: 12px;
  border: 1px solid $gray-200;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
  padding: 6px;
  margin-top: 6px;
}

:deep(.el-select-dropdown__item) {
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 2px;
  font-size: 0.9375rem;
  color: $gray-700;
  font-weight: 500;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:last-child {
    margin-bottom: 0;
  }

  &.is-hovering {
    background-color: rgba(1, 190, 176, 0.08);
    color: $color-teal-accent;
    transform: translateX(2px);
  }

  &.is-selected {
    background-color: rgba(1, 190, 176, 0.12);
    color: $color-teal-accent;
    font-weight: 600;
    position: relative;

    &::after {
      content: '✓';
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 1rem;
      font-weight: 700;
    }
  }

  &.is-disabled {
    color: $gray-300;
    cursor: not-allowed;

    &:hover {
      background-color: transparent;
      transform: none;
    }
  }
}
</style>
